import {
  Avatar,
  AvatarGroup,
  HStack,
  VStack,
  Text,
  Badge,
  BoxProps,
  TextProps,
} from '@chakra-ui/react';
import { CalendarIcon, EmailIcon } from 'assets/icons/v2';
import {
  WorksheetParticipant,
  WorksheetAnswerStatus,
} from 'hooks/api/backend/worksheets/useWorksheets';
import React, { ReactElement } from 'react';
import { getAvatarColorsFromName } from 'utils/getAvatarColorsFromName';

interface Props {
  participant: WorksheetParticipant;
}

const statusMap: Record<
  WorksheetAnswerStatus,
  { label: string; color: BoxProps['bg']; textColor: TextProps['color'] }
> = {
  [WorksheetAnswerStatus.SUBMITTED]: {
    label: 'Abgegeben',
    color: 'tag.background.green',
    textColor: 'tag.text.green',
  },
  [WorksheetAnswerStatus.IN_PROGRESS]: {
    label: 'In Bearbeitung',
    color: 'tag.background.yellow',
    textColor: 'tag.text.yellow',
  },
  [WorksheetAnswerStatus.NOT_STARTED]: {
    label: 'Nicht begonnen',
    color: 'text.default',
    textColor: 'text.tertiary',
  },
};

export const WorksheetParticipantItem = ({ participant }: Props): ReactElement => {
  const { firstName, lastName } = participant.userData;
  const status = participant.status ?? WorksheetAnswerStatus.NOT_STARTED;

  // Use submittedAt if SUBMITTED, otherwise updatedAt (last save), otherwise answeredAt (first answer)
  const displayDate =
    status === WorksheetAnswerStatus.SUBMITTED && participant.submittedAt
      ? participant.submittedAt
      : (participant.updatedAt ?? participant.answeredAt);

  const date = displayDate
    ? new Date(displayDate).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const badge = statusMap[status] ?? statusMap[WorksheetAnswerStatus.NOT_STARTED];
  const fullName = `${firstName} ${lastName}`;
  const avatarColors = getAvatarColorsFromName(fullName);

  return (
    <HStack
      width="100%"
      gap="m"
      py="m"
      borderBottom="default"
      borderColor="border.accent"
      align="flex-start"
    >
      <AvatarGroup size="md">
        <Avatar.Root bg={avatarColors.bg} color={avatarColors.color}>
          <Avatar.Fallback name={fullName} />
        </Avatar.Root>
      </AvatarGroup>
      <VStack align="flex-start" gap="2xs" flex={1}>
        <HStack>
          <Text fontWeight="semibold" color="text.secondary">
            {fullName}
          </Text>
          <Badge
            bg={badge.color}
            color={badge.textColor}
            borderRadius="m"
            px="xs"
            py="3xs"
            fontSize="12px"
            fontWeight="medium"
          >
            {badge.label}
          </Badge>
        </HStack>
        <HStack gap="xs" color="text.quaternary">
          <HStack gap="2xs">
            <EmailIcon boxSize="16px" color="icon.quaternary" />
            <Text fontSize="sm">{participant.userData.email}</Text>
          </HStack>
          <Text fontSize="sm">|</Text>
          <HStack gap="2xs">
            <CalendarIcon boxSize="16px" />
            <Text fontSize="sm">{date}</Text>
          </HStack>
        </HStack>
      </VStack>
    </HStack>
  );
};
